import * as yup from 'yup';
import { CANADIAN_AREA_CODES } from './personalInfoSchema';
export const supportTicketSchema = yup.object().shape({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be at most 100 characters')
    .matches(
      /^[a-zA-Z\s'-]+$/,
      'Name must contain only letters, spaces, hyphens, and apostrophes'
    ),

  email: yup
    .string()
    .email('Please enter a valid email address')
    .test('email-has-tld', 'Please enter a valid email address', (value) => {
      if (!value) return false;
      // Require a dot and at least 2 chars after it (e.g., ".com", ".net")
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
    })
    .required('Email is required')
    .max(255, 'Email must be at most 255 characters'),

  phone: yup
    .string()
    .optional()
    .test(
      'phone-format',
      'Phone number must be a valid Canadian number (format: +1XXXXXXXXXX)',
      function (value) {
        if (!value || value.trim() === '') return true; // Optional field
        const cleanPhone = value.replace(/\D/g, '');
        if (![10, 11].includes(cleanPhone.length)) return false;
        if (cleanPhone.length === 11 && !cleanPhone.startsWith('1'))
          return false;

        const areaCode =
          cleanPhone.length === 11
            ? cleanPhone.substring(1, 4)
            : cleanPhone.substring(0, 3);

        return CANADIAN_AREA_CODES.includes(areaCode);
      }
    ),

  subject: yup
    .string()
    .required('Subject is required')
    .min(5, 'Subject must be at least 5 characters')
    .max(100, 'Subject must be at most 100 characters')
    .matches(/^[a-zA-Z0-9\s\-_.,!?]+$/, 'Subject contains invalid characters'),

  category: yup
    .string()
    .required('Category is required')
    .oneOf(
      ['technical', 'billing', 'feature', 'general', 'membership'],
      'Please select a valid category'
    ),

  priority: yup
    .string()
    .required('Priority is required')
    .oneOf(['low', 'medium', 'high'], 'Please select a valid priority'),

  description: yup
    .string()
    .required('Please describe your problem')
    .min(10, 'Description must be at least 10 characters')
    .max(2000, 'Description must be at most 2000 characters')
    .test(
      'has-meaningful-content',
      'Description must contain meaningful words (at least 3 consecutive letters). Random characters and numbers are not sufficient.',
      function (value) {
        if (!value) return false;
        // Remove whitespace and check if there's any content
        const trimmedValue = value.trim();
        if (trimmedValue.length === 0) return false;
        // Check if there's at least one sequence of 3 or more consecutive letters
        // This ensures the description contains actual words, not just random characters
        // Examples that will be rejected: "@$#@54645n", "a1b2c3", "123abc123"
        // Examples that will be accepted: "I have a problem", "My issue", "login error"
        return /[a-zA-Z]{3,}/.test(trimmedValue);
      }
    ),

  attachments: yup
    .array()
    .of(
      yup
        .mixed()
        .test('file-type', 'Only image files are allowed', function (value) {
          if (!value) return true;
          return value instanceof File && value.type.startsWith('image/');
        })
        .test('file-size', 'File size must be less than 2MB', function (value) {
          if (!value) return true;
          return value instanceof File && value.size <= 2 * 1024 * 1024;
        })
    )
    .max(3, 'Maximum 3 attachments allowed')
    .optional(),
});

export type SupportTicketFormData = yup.InferType<typeof supportTicketSchema>;
